import type {
  ActiveSeasonView,
  ClaimDriverBody,
  GlobalActiveSeasonView,
  LobbyState,
  PlayConfig,
  QualLiveStandingsView,
  ReleaseClaimBody,
  SeasonCampaignDetail,
  SeasonCampaignSummary,
  SeasonMeetingCount,
  SeasonRegistrationStatusView,
  SimulateSessionResponse,
} from '@multirx/shared/types/championship.js';
import type { DivisionId } from '@multirx/shared/types/division.js';

const API = '/api';

/** Démarre ou reprend la saison pour les 4 divisions (D1–D4). */
export async function startOrResumeGlobalSeason(
  meetingCount?: SeasonMeetingCount,
  circuitCalendar?: number[]
): Promise<GlobalActiveSeasonView> {
  const body: { meetingCount?: number; circuitCalendar?: number[] } = {};
  if (meetingCount != null) body.meetingCount = meetingCount;
  if (circuitCalendar != null) body.circuitCalendar = circuitCalendar;
  const res = await fetch(`${API}/seasons`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Erreur saison');
  }
  return res.json();
}

/** Réserve une coquille de saison (D1–D4) — aucun roster/grille tant que
 * `lockAndStartGlobalSeason` n'a pas été appelé. */
export async function createGlobalSeasonShell(
  meetingCount?: SeasonMeetingCount,
  circuitCalendar?: number[]
): Promise<GlobalActiveSeasonView> {
  const body: { meetingCount?: number; circuitCalendar?: number[] } = {};
  if (meetingCount != null) body.meetingCount = meetingCount;
  if (circuitCalendar != null) body.circuitCalendar = circuitCalendar;
  const res = await fetch(`${API}/seasons/shell`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Erreur création saison');
  }
  return res.json();
}

/** Verrouille les inscriptions des 4 divisions et démarre la campagne. */
export async function lockAndStartGlobalSeason(): Promise<GlobalActiveSeasonView> {
  const res = await fetch(`${API}/seasons/lock`, { method: 'POST' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Erreur verrouillage saison');
  }
  return res.json();
}

/** Inscrit le pilote du joueur connecté à la saison en inscription de la division choisie. */
export async function joinSeasonRegistration(
  divisionId: DivisionId
): Promise<SeasonRegistrationStatusView> {
  const res = await fetch(`${API}/seasons/registration/join`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ divisionId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Inscription refusée');
  }
  return res.json();
}

export async function fetchSeasonRegistrationStatus(): Promise<SeasonRegistrationStatusView> {
  const res = await fetch(`${API}/seasons/registration/status`);
  if (!res.ok) throw new Error('Erreur statut inscription');
  return res.json();
}

/** @deprecated Préférer startOrResumeGlobalSeason */
export async function startOrResumeSeason(divisionId: DivisionId): Promise<ActiveSeasonView> {
  const res = await fetch(`${API}/seasons`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ divisionId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Erreur saison');
  }
  return res.json();
}

export async function fetchSeasonCampaigns(): Promise<SeasonCampaignSummary[]> {
  const res = await fetch(`${API}/seasons`);
  if (!res.ok) throw new Error('Erreur chargement historique saisons');
  return res.json();
}

export async function fetchSeasonCampaignDetail(
  campaignKey: string
): Promise<SeasonCampaignDetail> {
  const res = await fetch(`${API}/seasons/campaign/${encodeURIComponent(campaignKey)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Campagne introuvable');
  }
  return res.json();
}

/** Abandonne la campagne en cours (D1–D4) pour en lancer une autre. */
export async function cancelActiveGlobalSeason(): Promise<void> {
  const res = await fetch(`${API}/seasons/active`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Annulation impossible');
  }
}

export async function fetchActiveGlobalSeason(): Promise<GlobalActiveSeasonView | null> {
  const res = await fetch(`${API}/seasons/active`);
  if (!res.ok) throw new Error('Erreur chargement saison');
  const data = await res.json();
  return data;
}

export async function fetchQualLiveStandings(): Promise<
  Partial<Record<DivisionId, QualLiveStandingsView>>
> {
  const res = await fetch(`${API}/seasons/active/qual-standings`);
  if (!res.ok) throw new Error('Erreur chargement classement qualif');
  return res.json();
}

export async function fetchActiveSeason(division: DivisionId): Promise<ActiveSeasonView | null> {
  const res = await fetch(`${API}/seasons/active?division=${division}`);
  if (!res.ok) throw new Error('Erreur chargement saison');
  const data = await res.json();
  return data;
}

export async function fetchMeetingSession(
  sessionId: number,
  claimerId?: string
): Promise<{
  session: import('@multirx/shared/types/championship.js').MeetingSessionDetail;
  lobby: LobbyState;
}> {
  const q = claimerId ? `?claimerId=${encodeURIComponent(claimerId)}` : '';
  const res = await fetch(`${API}/meeting-sessions/${sessionId}${q}`);
  if (!res.ok) throw new Error('Session introuvable');
  return res.json();
}

export async function claimMeetingDriver(
  sessionId: number,
  body: ClaimDriverBody
): Promise<LobbyState> {
  const res = await fetch(`${API}/meeting-sessions/${sessionId}/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Claim refusé');
  }
  return res.json();
}

export async function releaseMeetingClaim(
  sessionId: number,
  body: ReleaseClaimBody
): Promise<LobbyState> {
  const res = await fetch(`${API}/meeting-sessions/${sessionId}/release`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Release refusé');
  return res.json();
}

export async function setMeetingClaimReady(
  sessionId: number,
  claimerId: string,
  ready = true
): Promise<LobbyState> {
  const res = await fetch(`${API}/meeting-sessions/${sessionId}/ready`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ claimerId, ready }),
  });
  if (!res.ok) throw new Error('Ready refusé');
  return res.json();
}

export async function simulateMeetingSession(sessionId: number): Promise<SimulateSessionResponse> {
  const res = await fetch(`${API}/meeting-sessions/${sessionId}/simulate`, { method: 'POST' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Simulation échouée');
  }
  return res.json();
}

export async function fetchMeetingPlayConfig(
  sessionId: number,
  driverId: number
): Promise<PlayConfig> {
  const res = await fetch(`${API}/meeting-sessions/${sessionId}/play-config?driverId=${driverId}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Config indisponible');
  }
  return res.json();
}

export async function fetchDriverSeasonPointBreakdown(
  seasonId: number,
  driverId: number
): Promise<import('@multirx/shared/game/seasonPointEvents.js').DriverSeasonPointsBreakdown> {
  const res = await fetch(`${API}/seasons/${seasonId}/drivers/${driverId}/point-events`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Détail points indisponible');
  }
  return res.json();
}

export async function fetchDriverSeasonProfile(
  seasonId: number,
  driverId: number
): Promise<import('@multirx/shared/game/driverPalmares.js').DriverSeasonProfile> {
  const res = await fetch(`${API}/seasons/${seasonId}/drivers/${driverId}/profile`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Profil pilote indisponible');
  }
  return res.json();
}
